import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import FormsyForm from 'formsy-react'
import { List, fromJS } from 'immutable'
import {
  DEFAULT_MONTH,
  MONTHS,
  formatUTC,
} from '../../../../services/date-time/index'
import {
  AMBASSADORS_PAGE_STATIC_KEY,
  PARTNER_SIGNUPS_TAB_VALUE,
  SIGNUPS_FIELD_CAMPAIGN,
  SIGNUPS_FIELD_CREATED,
  SIGNUPS_FIELD_EVENT,
  SIGNUPS_FIELD_ID,
  SIGNUPS_FIELD_MEDIUM,
  SIGNUPS_FIELD_PLAN,
  SIGNUPS_FIELD_SOURCE,
  SIGNUPS_MAX_PP,
  createCsvTitle,
  currentYear as year,
  getAmbassadorSignUp,
  getCsvHeaders,
  getSignUpsCsvData,
} from '../../../../services/ambassadors/index'
import Table from '../../../Table/index'
import { selectLocale } from '../../../../services/page/index'
import { exportCSVFile } from '../../../../services/file/index'
import { Select } from '../../../FormInput.v2/index'
import { pushHistory } from '../../../../services/navigation/actions'
import { getAmbassadorSignUps } from '../../../../services/ambassadors/actions'
import { selectStaticTextByKey } from '../../../../services/static-text/selectors'
import AmbassadorsPager from '../../AmbassadorsPager/index'
import AmbassadorsYearDropdown from '../../AmbassadorsYearDropdown/index'
import {
  selectAmbassadorDetailsData,
  selectAmbassadorSignUpsData,
  selectAmbassadorSignUpsError,
  selectAmbassadorSignUpsProcessing,
  selectAmbassadorTotalSignUps,
} from '../../../../services/ambassadors/selectors'
import { selectUserLanguagePrimary } from '../../../../services/user/index'
import { selectAuth } from '../../../../services/auth/index'
import { selectLocation } from '../../../../services/resolver/selectors'

/**
 * Determine the Plan header
 * @param {Number} contentSetId
 * @returns {String} Plan header
 */
function getPlan (contentSetId = 0) {
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  return (contentSetId === 290 && staticText.get('events', ''))
    || (contentSetId === 280 && staticText.get('annual', ''))
    || (contentSetId === 253 && staticText.get('monthly', ''))
    || staticText.get('na', '')
}

/**
 * Columns for the table
 * @returns {Array} array with the colums
 */
const getColumns = () => {
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  const language = useSelector(selectUserLanguagePrimary)

  return [
    {
      key: SIGNUPS_FIELD_CREATED,
      title: staticText.get('signupsCreatedColumn', ''),
      render: (value) => (value ? formatUTC(value, language) : ''),
    },
    {
      key: SIGNUPS_FIELD_PLAN,
      title: staticText.get('signupsPlanColumn', ''),
      render: (value, item) => {
        const contentSetId = item.get('planContentSetId', 0)
        const heading = getPlan(contentSetId)
        return (
          <div className="partner-signups-tab__plan">
            {heading && <div className="partner-signups-tab__plan-heading">{heading}</div>}
            <div className="partner-signups-tab__plan-name">{value}</div>
          </div>
        )
      },
    },
    {
      key: SIGNUPS_FIELD_EVENT,
      title: staticText.get('signupsEventColumn', ''),
    },
    {
      key: SIGNUPS_FIELD_CAMPAIGN,
      title: staticText.get('signupsCampaignColumn', ''),
      render: (value, item) => {
        const utmCampaign = item.getIn(['campaign', 'utmCampaign'], '')
        const campaignName = item.getIn(['campaign', 'campaignName'], '')
        return utmCampaign
          ? (
            <a href={`ambassadors?tab=partner-campaigns&utmCampaign=${utmCampaign}`}>
              <b>{campaignName}</b>
            </a>
          ) : value
      },
    },
    {
      key: SIGNUPS_FIELD_SOURCE,
      title: staticText.get('signupsSourceColumn', ''),
      render: (value, item) => {
        const sourceDetails = item.get('sourceDetails')
        return `${value}${sourceDetails ? `: ${sourceDetails}` : ''}`
      },
    },
    {
      key: SIGNUPS_FIELD_MEDIUM,
      title: staticText.get('signupsIdColumn', ''),
    },
  ]
}

/**
 * Series Seasons Count
 * @param {Object} props - The props
 * @param {Integer} totalSeasons - total seasons for the current series
 * @returns {JSX.Element} - Series season count
 */
function MonthFilter (props) {
  const {
    staticText,
    onChangeHandler,
  } = props
  const defaultOption = MONTHS[0]

  // Changed due to an issue with several events fired by FormsyForm onChange event
  // This resulted in multiple requests being sent when the month is changed
  // Select onChange handler is used instead
  return (
    <FormsyForm
      className="partner-signups-tab__month-dropdown"
      noValidate
    >
      <div className="partner-signups-tab__month-select">
        <Select
          name="month"
          value={defaultOption}
          placeholder={staticText.get(defaultOption, defaultOption)}
          label={staticText.get('ambassadorSelectMonthLabel', '')}
          onChange={onChangeHandler}
        >
          {MONTHS.map((month, index) => (
            <option value={`${index}`} key={`months-${index}`}>
              {staticText.get(month, month)}
            </option>
          ))}
        </Select>
      </div>
    </FormsyForm>
  )
}

/**
 * Tab component with all the SignUps data
 * @param {Map} history The history
 * @returns {React.Component}
 */
const PartnerSignupsTab = (props) => {
  const { history } = props

  const location = useSelector(selectLocation)
  const language = useSelector(selectUserLanguagePrimary)
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))
  const dispatch = useDispatch()

  const signUpsData = useSelector(selectAmbassadorSignUpsData)
  const ambassadorDetailsData = useSelector(selectAmbassadorDetailsData)
  const isSignUpsDataProcessing = useSelector(selectAmbassadorSignUpsProcessing)
  const signUpsError = useSelector(selectAmbassadorSignUpsError)
  const locale = useSelector(selectLocale)
  const auth = useSelector(selectAuth)

  const columns = getColumns()
  const totalSignUps = useSelector(selectAmbassadorTotalSignUps)

  const { query = {}, pathname } = location
  const { p: queryPage = 1 } = query

  const [currentYear, setCurrentYear] = useState(year)
  const [currentMonth, setCurrentMonth] = useState(DEFAULT_MONTH.toLowerCase())
  const [currentPage, setCurrentPage] = useState(parseInt(queryPage, 10))

  const dispatchHistory = (options) => {
    dispatch(pushHistory({
      query: { ...query, ...options, tab: PARTNER_SIGNUPS_TAB_VALUE },
      scrollToTop: true,
      url: pathname,
    }))
  }

  const handleChangeYear = (selectedYear) => {
    setCurrentYear(selectedYear)
    dispatchHistory({ p: 1, year: selectedYear })
  }

  const handleChangeMonth = (selectedMonth) => {
    setCurrentMonth(selectedMonth)
    dispatchHistory({ p: 1, month: selectedMonth })
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    dispatchHistory({ p: page })
  }

  const handleTableOrder = () => {
    dispatch(getAmbassadorSignUps({
      year: currentYear,
      month: currentMonth,
      p: currentPage,
    }))
  }

  const handleExport = async () => {
    const headers = getCsvHeaders(columns)
    const csvSignupsRawData = await getAmbassadorSignUp({
      auth,
      year: currentYear,
      month: currentMonth,
      p: 1,
      pp: totalSignUps,
    })
    const csvSignupsData = csvSignupsRawData ? fromJS(csvSignupsRawData).get('data', List()) : List()
    const csvData = getSignUpsCsvData(csvSignupsData, language).toJS()

    const csvTitle = createCsvTitle({
      prefix: staticText.get('csvPartnerPrefix'),
      type: staticText.get('csvSignupsTitle'),
      downloaded: staticText.get('Downloaded'),
      locale,
      name: ambassadorDetailsData.get('name', ''),
      year: currentYear,
      month: Number(currentMonth)
        ? staticText.get(MONTHS[currentMonth])
        : staticText.get(MONTHS[0]),
    })

    exportCSVFile(headers, csvData, csvTitle)
  }

  if (signUpsError) {
    return (
      <div className="partner-signups-tab">
        <div className="ambassadors-page__error">
          {staticText.get('somethingWentWrong')}
        </div>
      </div>
    )
  }

  return (
    <div className="partner-signups-tab">
      <div className="partner-signups-tab__actions">
        <MonthFilter
          staticText={staticText}
          onChangeHandler={handleChangeMonth}
        />
        <AmbassadorsYearDropdown
          onSubmit={handleExport}
          onChange={handleChangeYear}
          buttonClass="form-button--secondary partner-signups-tab__download"
        />
      </div>
      <div className="partner-signups-tab__table">
        <Table
          columns={columns}
          tableData={signUpsData}
          history={history}
          rowKey={SIGNUPS_FIELD_ID}
          historyCallback={handleTableOrder}
          loading={isSignUpsDataProcessing}
        />
      </div>
      <div className="partner-signups-tab__pagination">
        <AmbassadorsPager
          onChangePage={(next) => handlePageChange(next)}
          pp={SIGNUPS_MAX_PP}
          total={totalSignUps}
          p={parseInt(queryPage, 10)}
        />
      </div>
    </div>
  )
}

export default PartnerSignupsTab
